import {createContext, useContext} from 'react';
import type {AiChatActionsContextType, AiChatContextType, AiChatStateContextType} from '@/ai-chat/providers/ai-chat-provider.types';

// Split into two contexts so action-only consumers (via useAiChatActions) do not re-render
// when provider STATE changes — only the state context churns on state updates, while the
// actions context value is reference-stable.
export const aiChatStateContext = createContext<AiChatStateContextType | undefined>(undefined);
export const aiChatActionsContext = createContext<AiChatActionsContextType | undefined>(undefined);

export function useAiChatState(): AiChatStateContextType {
  const state = useContext(aiChatStateContext);

  if (state === undefined) {
    throw new Error('useAiChatState must be used within an AiChatProvider');
  }

  return state;
}

export function useAiChatActions(): AiChatActionsContextType {
  const actions = useContext(aiChatActionsContext);

  if (actions === undefined) {
    throw new Error('useAiChatActions must be used within an AiChatProvider');
  }

  return actions;
}

/**
 * Combined convenience hook for consumers that need both halves. It subscribes to BOTH
 * contexts, so it re-renders on state changes — components that only need actions should
 * use useAiChatActions() instead to skip those re-renders.
 */
export function useAiChat(): AiChatContextType {
  return {state: useAiChatState(), actions: useAiChatActions()};
}
